import { useEffect, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  ChevronRight,
  Copy,
  LifeBuoy,
  RefreshCcw,
} from "lucide-react";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import { Button } from "./ui/button";
import { registrarErro } from "@/lib/error-log";
import { StatusHeading, StatusScreen } from "./status-screen";

const SUPORTE_WHATSAPP = "5519993376029";

/** Código curto para o usuário citar no suporte: 4 hex do hash da mensagem
 *  (a mesma falha gera sempre o mesmo prefixo) + o minuto em base36. */
function gerarCodigo(mensagem: string) {
  let h = 0;
  for (let i = 0; i < mensagem.length; i++) {
    h = (h * 31 + mensagem.charCodeAt(i)) | 0;
  }
  const hex = Math.abs(h)
    .toString(16)
    .toUpperCase()
    .padStart(4, "0")
    .slice(0, 4);
  const minuto = Math.floor(Date.now() / 60000)
    .toString(36)
    .toUpperCase()
    .slice(-4);
  return `EC-${hex}-${minuto}`;
}

export function RootErrorBoundary() {
  const error = useRouteError();

  // Erro de chunk: acontece quando uma versão nova foi publicada e o navegador
  // ainda pede os arquivos da anterior. Não é falha do usuário nem do sistema.
  const isChunkError =
    error instanceof Error &&
    (error.message.includes("dynamically imported module") ||
      error.message.includes("Importing a module script failed") ||
      error.message.includes("Failed to fetch"));

  if (isChunkError) {
    return (
      <StatusScreen tone="brand">
        <StatusHeading
          tone="brand"
          eyebrow="Atualização disponível"
          title="Uma nova versão acabou de ser publicada"
          icon={<RefreshCcw className="size-5" />}
        />

        <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
          Seu navegador ainda está com os arquivos da versão anterior.
          Recarregue a página para pegar a mais recente — leva um instante, e
          nada do que você já salvou é afetado.
        </p>

        {/* O mesmo filete do loader do sistema: sinaliza "em transição" sem um
            disco girando. */}
        <div className="mt-6 flex items-center gap-3">
          <div className="ecclesiato-loader" />
          <span className="text-xs text-muted-foreground">
            Versão nova esperando por você
          </span>
        </div>

        <div className="mt-7">
          <Button size="lg" onClick={() => window.location.reload()}>
            <RefreshCcw className="size-4" />
            Recarregar agora
          </Button>
        </div>
      </StatusScreen>
    );
  }

  let errorMessage = "Ocorreu um erro inesperado.";
  let status: number | null = null;
  if (isRouteErrorResponse(error)) {
    status = error.status;
    errorMessage = error.statusText || error.data?.message || errorMessage;
  } else if (error instanceof Error) {
    errorMessage = error.message;
  } else if (typeof error === "string") {
    errorMessage = error;
  }

  return (
    <ErroInesperado error={error} message={errorMessage} status={status} />
  );
}

/** Separado do boundary porque tem estado (código do erro e "copiado"), e o
 *  ramo do chunk error retorna antes de qualquer hook desses existir. */
function ErroInesperado({
  error,
  message,
  status,
}: {
  error: unknown;
  message: string;
  status: number | null;
}) {
  const [codigo] = useState(() => gerarCodigo(message));
  const [copiado, setCopiado] = useState(false);

  const stack = error instanceof Error ? error.stack : undefined;

  // Grava a falha para você poder investigar depois — o usuário não vê nada
  // disso. `registrarErro` nunca rejeita e deduplica por código na sessão, por
  // isso não há nada a tratar aqui.
  useEffect(() => {
    void registrarErro({ codigo, mensagem: message, stack, status });
  }, [codigo, message, stack, status]);

  // O que vai para a área de transferência: tudo que o suporte precisa para
  // localizar a falha, não só o código na tela.
  const relatorio = [
    `Código: ${codigo}`,
    status ? `Status: ${status}` : null,
    `URL: ${window.location.href}`,
    `Mensagem: ${message}`,
    stack ? `\n${stack}` : null,
  ]
    .filter(Boolean)
    .join("\n");

  const copiar = () => {
    void navigator.clipboard?.writeText(relatorio).then(() => {
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 2000);
    });
  };

  const whatsapp = `https://wa.me/${SUPORTE_WHATSAPP}?text=${encodeURIComponent(
    `Olá! Encontrei um erro no Ecclesiato. Código: ${codigo}`,
  )}`;

  return (
    <StatusScreen tone="danger" role="alert">
      <StatusHeading
        tone="danger"
        eyebrow={status ? `Erro ${status}` : "Erro inesperado"}
        title="Algo saiu do lugar nesta tela"
        icon={<AlertTriangle className="size-5" />}
      />

      <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
        Não conseguimos terminar de carregar esta página. Seus dados continuam
        salvos — nada do que você registrou foi perdido. Nossa equipe já foi
        avisada; tente novamente e, se o erro voltar, fale com o suporte.
      </p>

      {import.meta.env.DEV && (
        <details className="group mt-6 overflow-hidden rounded-xl border border-border/60 bg-muted/30">
          <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-2.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground">
            <ChevronRight className="size-3.5 transition-transform group-open:rotate-90" />
            Detalhes técnicos
            <span className="ml-auto font-mono text-[10px] tracking-wider uppercase opacity-60">
              dev
            </span>
          </summary>
          <pre className="max-h-72 overflow-auto border-t border-border/60 bg-background/40 p-4 font-mono text-[11px] leading-relaxed whitespace-pre-wrap text-foreground/75">
            {message}
            {stack && `\n\n${stack}`}
          </pre>
        </details>
      )}

      <div className="mt-7 flex flex-col gap-2 sm:flex-row">
        <Button size="lg" onClick={() => window.location.reload()}>
          <RefreshCcw className="size-4" />
          Tentar novamente
        </Button>
        <Button
          size="lg"
          variant="outline"
          onClick={() => window.history.back()}
        >
          <ArrowLeft className="size-4" />
          Voltar
        </Button>
        <Button
          size="lg"
          variant="ghost"
          asChild
          className="text-muted-foreground hover:text-foreground sm:ml-auto"
        >
          <a href={whatsapp} target="_blank" rel="noopener noreferrer">
            <LifeBuoy className="size-4" />
            Suporte
          </a>
        </Button>
      </div>

      {/* Referência: fica no rodapé, no menor corpo da tela, porque é a única
          coisa técnica que sobra — e ela existe para o suporte, não para quem
          está olhando. O link do WhatsApp acima já leva o código embutido, então
          quem clica em "Suporte" nunca precisa ler esta linha. */}
      <div className="mt-6 flex items-center gap-2 border-t border-border/50 pt-4">
        <span className="text-[11px] text-muted-foreground/70">Referência</span>
        <button
          type="button"
          onClick={copiar}
          title="Copiar detalhes para o suporte"
          className="inline-flex items-center gap-1.5 rounded font-mono text-[11px] text-muted-foreground/70 transition-colors hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
        >
          {codigo}
          {copiado ? (
            <Check className="size-3 text-primary" />
          ) : (
            <Copy className="size-3 opacity-50" />
          )}
        </button>
      </div>
    </StatusScreen>
  );
}
